import { type ReactNode, useEffect, useState } from 'react';
import { faCheck } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MutedText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { FormFieldLegend } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

type RadioOption = {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  preview?: ReactNode;
  disabled?: boolean;
};

type RadioCardGroupProps = Omit<React.ComponentProps<typeof RadioGroup>, 'onValueChange'> &
  BaseFormFieldProps & {
    options: RadioOption[];
    onValueChange?: (value: string) => void;
    className?: string;
    radioPosition?: 'left' | 'top-right';
    /** Grid columns class (e.g., "grid-cols-2", "sm:grid-cols-2 md:grid-cols-3"). Defaults to "grid-cols-2". */
    gridColumns?: string;
    /** Gap class for the grid. Defaults to "gap-2". */
    gridGap?: string;
    /** Hide the radio indicator visually while keeping it accessible. */
    hideRadio?: boolean;
    /** Extra classes applied to each card div (overrides inner padding etc.). */
    cardClassName?: string;
    variant?: 'default' | 'preview';
  };

const previewCardClasses = [
  'relative flex flex-col items-center justify-center gap-2 bg-background py-4',
  'hover:bg-accent',
  'has-data-[state=checked]:border-2',
  'has-[[data-slot=radio-group-item]:focus-visible]:border-ring',
  'has-[[data-slot=radio-group-item]:focus-visible]:ring-[3px]',
  'has-[[data-slot=radio-group-item]:focus-visible]:ring-ring/50',
];

const defaultCardClasses = {
  topRight: 'relative flex flex-col items-center gap-3 p-4',
  left: 'flex items-start gap-3 p-3',
};

const radioPositionClasses = {
  preview: 'sr-only',
  topRight: 'absolute top-3 right-3',
  left: 'pt-0.5',
};

export function RadioCardGroup({
  id,
  label,
  helperText,
  errorText,
  isRequired,
  showAsterisk,
  options,
  className,
  onValueChange,
  gridColumns = 'grid-cols-1 sm:grid-cols-2',
  gridGap = 'gap-2',
  radioPosition = 'left',
  hideRadio = false,
  cardClassName,
  variant = 'default',
  ...groupProps
}: RadioCardGroupProps) {
  const {
    fieldId: groupId,
    helperId,
    errorId,
    describedBy,
  } = useFormField(id, helperText, errorText);
  const invalid = Boolean(errorText);
  const [selectedValue, setSelectedValue] = useState(
    groupProps.value ?? groupProps.defaultValue ?? '',
  );

  useEffect(() => {
    if (groupProps.value !== undefined) {
      setSelectedValue(groupProps.value ?? '');
    }
  }, [groupProps.value]);

  const handleValueChange = (value: string) => {
    if (groupProps.value === undefined) {
      setSelectedValue(value);
    }
    onValueChange?.(value);
  };
  const labelId = label ? `${groupId}-legend` : undefined;

  return (
    <fieldset
      className={cn('grid w-full gap-1.5', className)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      id={groupId}
    >
      {label && <FormFieldLegend id={labelId} label={label} showAsterisk={showAsterisk} />}
      {helperText && <FormFieldMessages helperText={helperText} helperId={helperId} />}
      <RadioGroup
        aria-labelledby={labelId}
        aria-required={isRequired}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onValueChange={handleValueChange}
        className={cn('grid', gridColumns, gridGap)}
        {...groupProps}
      >
        {options.map((option) => {
          const isPreviewVariant = variant === 'preview';
          const itemId = `${groupId}-${option.value}`;
          const useTopRight =
            !isPreviewVariant && (radioPosition === 'top-right' || Boolean(option.icon));

          return (
            <Label
              key={option.value}
              htmlFor={itemId}
              className={cn('cursor-pointer', option.disabled && 'cursor-not-allowed')}
            >
              <div
                className={cn(
                  'rounded-md border transition-colors',
                  'has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5',
                  isPreviewVariant
                    ? previewCardClasses
                    : useTopRight
                      ? cn(defaultCardClasses.topRight, cardClassName)
                      : cn(defaultCardClasses.left, cardClassName),
                  option.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <div
                  className={cn(
                    isPreviewVariant
                      ? radioPositionClasses.preview
                      : useTopRight
                        ? radioPositionClasses.topRight
                        : radioPositionClasses.left,
                    hideRadio && 'sr-only',
                  )}
                >
                  <RadioGroupItem
                    value={option.value}
                    id={itemId}
                    disabled={option.disabled}
                    className={cn(isPreviewVariant && radioPositionClasses.preview)}
                  />
                </div>
                {isPreviewVariant ? (option.preview ?? option.icon) : option.icon}
                <div
                  className={cn(
                    'flex flex-col gap-1',
                    (isPreviewVariant || useTopRight) && 'text-center',
                    isPreviewVariant && 'items-center',
                  )}
                >
                  <div
                    className={cn(isPreviewVariant ? 'text-xs font-medium' : 'text-sm font-medium')}
                  >
                    {option.title}
                  </div>
                  {option.description ? (
                    <MutedText className="text-xs">{option.description}</MutedText>
                  ) : null}
                </div>
                {isPreviewVariant && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute bottom-1.5 right-1.5 grid size-4 place-content-center rounded-full bg-primary text-primary-foreground transition-opacity',
                      selectedValue === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <Icon icon={faCheck} className="text-[8px]" />
                  </span>
                )}
              </div>
            </Label>
          );
        })}
      </RadioGroup>
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </fieldset>
  );
}
